// Block D (2026-10-08): the Contacts page on the v2 kit, after design/business/mockups/contacts.html.
//
// Header (subline from the list response), tag tabs with counts and Edit tags, the "Needs follow up
// (N)" chip (FU-1, decision 43; opened by the Dashboard and by ?contact_filter=follow-up), sort,
// Cards / List, search by name, email, company or tag; a card grid or a ListRow list (stacked card
// rows on a phone); the contact in a SidePanel drawer (full screen on a phone); delete with Undo
// (decision 7); Import CSV (Business). Personal keeps its categories and has no Import, CRM or money.
import { useState, useEffect, useMemo, useCallback } from "react";
import { describeWriteFailure } from '../utils/writeFailure';   // a failed write says so, where the control is
import { showErrorToast, showToast } from '../utils/toast';
import { messageFromError } from '../utils/apiError';
import { api } from "../api/client";
import { useContacts } from "../contexts/ContactsContext";
import {
  PageLayout, PageHeader, Card, Button, TabGroup, Chip, SegmentedControl, Select, SearchInput,
  ConfirmDialog, Avatar, EmptyState, ErrorState, Skeleton, List, ListRow, RowPrimary, Banner, useDelayedDelete, useBreakpoint, v, HeaderButton,
} from '../ui';
import PhoneListFilters from './common/PhoneListFilters';   // fresh-eyes item 21
import { useFollowUps, FollowUpList } from "./FollowUp/FollowUpPanel"; // FU-1: the "Needs follow up" chip
import { useDeepLinkTarget, useDeepLinkItem, clearPendingDeepLink } from '../utils/deepLink'; // Block 203a: open one contact from a follow-up nudge
import ContactFormModal from './Contacts/ContactFormModal';
import ContactDrawer, { moneyLabel } from './Contacts/ContactDrawer';
import ContactPage from './Contacts/ContactPage';   // the full contact page, /contacts/<id>
import { CONTACTS_PATH, contactIdFromPath, contactTabFromSearch, contactPagePath, openContactPage, takePendingTab } from './Contacts/contactActions';
import ImportContacts from './Contacts/ImportContacts';
import TagEditor from './Contacts/TagEditor';
import MergeReview from './Contacts/MergeReview';
import { PRESET_TAGS, PERSONAL_CATEGORIES, tagColor, primaryTag, orderedTags } from './Contacts/contactTags';
import './Contacts/Contacts.css';

// The "Needs follow up" chip's filter value: not a tag, so it can never clash with one.
const FOLLOW_UP_FILTER = "__needs_follow_up";

const VIEW_KEY = 'aidey.contacts.view';
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards'; } catch { return 'cards'; } };
const saveView = (v2) => { try { localStorage.setItem(VIEW_KEY, v2); } catch { /* per-viewer convenience only */ } };

// "Last contact today", "3 days ago", "5 weeks ago", else the date. Never an invented recency.
export function lastContactLabel(iso, now = new Date()) {
  if (!iso) return 'No contact yet';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'No contact yet';
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const SORTS = {
  recent: (a, b) => (new Date(b.lastContactedAt || 0) - new Date(a.lastContactedAt || 0)) || a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' }),
  value: (a, b) => ((b.lifetimeValue ?? -1) - (a.lifetimeValue ?? -1)) || a.name.localeCompare(b.name),
};

function valueText(c) {
  if (c.lifetimeValue === undefined) return null;
  if (c.lifetimeValue === null) return 'Value unavailable';
  return `${moneyLabel(c.lifetimeValue, c.lifetimeCurrency || 'GBP')} lifetime`;
}

function navigateTo(path) {
  window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: path }));
}

// ─── Main Component ───────────────────────────────────────────────────────────
function ContactsDashboard({ isPersonal = false }) {
  const { contacts, setContacts, refresh, loadFailed: contactsLoadFailed, loaded, canDeleteContacts } = useContacts();
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("All");
  const [sort, setSort] = useState("recent");
  const phone = useBreakpoint() === 'phone';   // fresh-eyes item 21: search first, the rest behind Filter
  const [view, setViewState] = useState(readView);
  const setView = (x) => { setViewState(x); saveView(x); };
  const [selectedId, setSelectedId] = useState(null);
  const [startWith, setStartWith] = useState(null);   // 'email': the pop-up opens with the composer
  // Block 203a: a follow-up nudge deep-links to one contact. Same three-step contract every
  // other section uses (utils/deepLink.js). Waits for contacts to load, then opens the drawer,
  // and only retires the link once it has actually opened.
  // Item 8 (2026-10-12): once loaded, the contact opens, or "This item is no longer available".
  useDeepLinkItem('contact', {
    ready: loaded !== false && !contactsLoadFailed,
    find: (id) => contacts.find(x => String(x.id) === id),
    // From the full page too (a search while one is open): the list, with this contact's pop-up.
    open: (c) => {
      if (contactIdFromPath(window.location.pathname)) { try { window.history.replaceState({}, '', CONTACTS_PATH); } catch { /* ignore */ } }
      setPageAt(null);
      setSelectedId(c.id);
    },
  });
  // The full contact page: /contacts/<id>[?tab=], read from the address (a reload, a link, Back and
  // Forward, the sidebar's Contacts) and written by "Open full profile", "View all …" and the tabs.
  const readPage = () => {
    const id = contactIdFromPath(window.location.pathname);
    return id ? { id, tab: takePendingTab() || contactTabFromSearch(window.location.search) } : null;
  };
  const [pageAt, setPageAt] = useState(readPage);
  useEffect(() => {
    const onPath = () => { if (/^\/contacts(\/|$)/.test(window.location.pathname)) setPageAt(readPage()); };
    window.addEventListener('popstate', onPath);
    window.addEventListener('aidey:pathchange', onPath);
    return () => { window.removeEventListener('popstate', onPath); window.removeEventListener('aidey:pathchange', onPath); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const showPageTab = (id, tab) => {
    try { window.history.replaceState(window.history.state, '', contactPagePath(id, tab)); } catch { /* ignore */ }
    setPageAt({ id, tab });
  };
  const backToList = () => {
    try { window.history.pushState({}, '', CONTACTS_PATH); } catch { /* ignore */ }
    setPageAt(null);
  };
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  // FU-1 (decision 43): "Needs follow up (N)", business only. One fetch gives the chip its true count
  // and the list its contacts. Opened already filtered by the Dashboard's "See all (N)" and by
  // /contacts?contact_filter=follow-up (the same deep-link contract as ?contact=).
  const followUps = useFollowUps({ enabled: !isPersonal });
  const [dlFilter, dlClearFilter] = useDeepLinkTarget('contact_filter');
  useEffect(() => {
    if (dlFilter === 'follow-up' && !isPersonal) { setTagFilter(FOLLOW_UP_FILTER); dlClearFilter(); clearPendingDeepLink(); }
  }, [dlFilter, isPersonal]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showTagEditor, setShowTagEditor] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ── Custom tags and tag colours (preferences on the user) ────────────────────
  // A FAILED LOAD IS SAID. The old load swallowed it, so custom tags silently vanished from the
  // tabs and the tag manager offered to edit a list it had never read.
  const [customTags, setCustomTags] = useState([]);
  const [tagColors, setTagColors] = useState({});
  const [canRecolour, setCanRecolour] = useState(false);
  const [prefsError, setPrefsError] = useState(null);
  const loadPrefs = useCallback(() => {
    if (isPersonal) return;
    setPrefsError(null);
    api('/api/users/me')
      .then(res => res.json())
      .then(data => {
        const prefs = data?.user?.preferences || {};
        setCustomTags(Array.isArray(prefs.crmCustomTags) ? prefs.crmCustomTags : []);
      })
      .catch(err => setPrefsError(messageFromError(err, 'Your custom tags could not be loaded.')));
    // Decision 83: tag colours are the business's (the same for everyone), set by the owner or an
    // admin. Until they load, every tag shows its default colour; a failed load is said in the editor.
    api('/api/contacts/tag-colors')
      .then(res => res.json())
      .then(data => { setTagColors(data?.colors && typeof data.colors === 'object' ? data.colors : {}); setCanRecolour(!!data?.canEdit); })
      .catch(err => setPrefsError(messageFromError(err, 'The tag colours could not be loaded.')));
  }, [isPersonal]);
  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  // Saves first, then shows: a tag is in the list only once the server has it.
  async function savePrefs(patch) {
    await api('/api/users/me', { method: 'PUT', body: JSON.stringify({ preferences: patch }) });
  }
  async function addCustomTag(t) {
    const updated = [...customTags, t];
    try { await savePrefs({ crmCustomTags: updated }); } catch (err) { throw new Error(describeWriteFailure(err, 'add that tag')); }
    setCustomTags(updated);
  }
  async function recolourTag(tag, color) {
    let saved;
    try {
      saved = await (await api('/api/contacts/tag-colors', { method: 'PUT', body: JSON.stringify({ tag, color }) })).json();
    } catch (err) { throw new Error(describeWriteFailure(err, 'change that colour')); }
    // What the server now holds, so a colleague's change made meanwhile shows too.
    setTagColors(saved?.colors && typeof saved.colors === 'object' ? saved.colors : { ...tagColors, [tag]: color });
  }
  // Takes the tag off every contact that has it (the normal contact save), then off the list.
  async function removeTag(tag) {
    const carrying = contacts.filter(c => (c.tags || []).includes(tag));
    const results = await Promise.allSettled(carrying.map(c =>
      api(`/api/contacts/${c.id}`, { method: 'PUT', body: JSON.stringify({ tags: (c.tags || []).filter(t => t !== tag) }) })));
    const done = new Set(carrying.filter((c, i) => results[i].status === 'fulfilled').map(c => c.id));
    setContacts(prev => prev.map(c => (done.has(c.id) ? { ...c, tags: (c.tags || []).filter(t => t !== tag) } : c)));
    const failed = carrying.length - done.size;
    if (failed) {
      throw new Error(`Taken off ${done.size} of ${carrying.length} contacts; ${failed} could not be changed, so the tag is still there. Try again.`);
    }
    if (customTags.includes(tag)) {
      const updated = customTags.filter(t => t !== tag);
      try { await savePrefs({ crmCustomTags: updated }); } catch (err) {
        throw new Error(`Taken off all ${carrying.length} contacts, but ${describeWriteFailure(err, 'remove it from your tag list').replace(/^Could not/, 'could not')}`);
      }
      setCustomTags(updated);
    }
    if (tagFilter === tag) setTagFilter("All");
    showToast(`Removed the tag "${tag}"${carrying.length ? ` from ${carrying.length} contact${carrying.length === 1 ? '' : 's'}` : ''}.`, { type: 'success' });
  }

  // ── Delete with Undo (decision 7) ───────────────────────────────────────────
  // The contact disappears at once; the DELETE is sent only after the 6 second Undo window (or at
  // once, keepalive, if the page goes away). A failed DELETE brings it back with the server's words.
  const remover = useDelayedDelete({
    send: (c, opts) => deleteContact(c, opts),
    onDeleted: (c) => setContacts(prev => prev.filter(x => x.id !== c.id)),
    describe: (c) => `${c.name} deleted.`,
    failMessage: 'That contact could not be deleted.',
  });
  const { isHidden } = remover;

  // ── Duplicates: Review & merge (decision 79) ────────────────────────────────
  // The owner or an admin sees "N possible duplicates". A merge waits out the Undo window like a
  // delete: the merged-away record disappears at once, and only then is the merge sent. If it
  // fails, the record comes back and the toast says the server's words.
  const [dupes, setDupes] = useState({ pairs: [], canMerge: false });
  const [showMerge, setShowMerge] = useState(false);
  const loadDupes = useCallback(() => {
    if (isPersonal) return;
    api('/api/contacts/duplicates').then(r => r.json()).then(d => setDupes({ pairs: d?.pairs || [], canMerge: !!d?.canMerge })).catch(() => {});
  }, [isPersonal]);
  useEffect(() => { loadDupes(); }, [loadDupes, contacts.length]);
  const merger = useDelayedDelete({
    send: (item, { keepalive }) => api(`/api/contacts/${item.keep.id}/merge`, { method: 'POST', body: JSON.stringify({ otherId: item.other.id }), keepalive }),
    onDeleted: (item) => { setContacts(prev => prev.filter(x => x.id !== item.other.id)); refresh(); loadDupes(); },
    describe: (item) => `${item.other.name} merged into ${item.keep.name}.`,
    failMessage: 'Those contacts could not be merged. Nothing was changed.',
  });
  const isMerged = merger.isHidden;
  const openDupes = dupes.pairs.filter(p => !isHidden(p.a.id) && !isHidden(p.b.id) && !isMerged(p.a.id) && !isMerged(p.b.id));
  const visible = useMemo(() => contacts.filter(c => !isHidden(c.id) && !isMerged(c.id)), [contacts, isHidden, isMerged]);

  const allTags = [...PRESET_TAGS, ...customTags.filter(t => !PRESET_TAGS.includes(t))];

  const tagCounts = useMemo(() => {
    const counts = {};
    visible.forEach(c => (c.tags || []).forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
    return counts;
  }, [visible]);
  const tagsInUse = Object.keys(tagCounts);
  const newCount = visible.filter(c => c.isNew).length;
  const untaggedCount = visible.filter(c => !(c.tags || []).length).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visible.filter(c => {
      const matchSearch = !q
        || c.name?.toLowerCase().includes(q)
        || c.email?.toLowerCase().includes(q)
        || c.company?.toLowerCase().includes(q)
        || (c.tags || []).some(t => t.toLowerCase().includes(q));
      const matchTag = tagFilter === "All"
        ? true
        : tagFilter === "New"
          ? c.isNew
          : tagFilter === "Untagged"
            ? !(c.tags || []).length
            : (c.tags || []).includes(tagFilter);
      return matchSearch && matchTag;
    }).sort(SORTS[sort] || SORTS.recent);
  }, [visible, search, tagFilter, sort]);

  // ── A CONTACT THAT WAS NEVER SAVED LOOKED SAVED (2026-10-04) ──────────────
  //
  // All three of these wrote optimistically and then fired an un-awaited api() with
  // `.catch(() => {})`. Closing the form and showing the new row in the list IS the success
  // signal, and it appeared with the write failed. This is her customer's client data.
  //
  // The optimistic update stays, but every one of them rolls back and says what did not
  // happen. The FORM STAYS OPEN on a failed save, so the details she typed are not lost.
  async function saveContact(contact) {
    const isNew = !contacts.find(c => c.id === contact.id);
    const snapshot = contacts;
    const previousSelected = selectedId;
    setContacts(prev => {
      const idx = prev.findIndex(c => c.id === contact.id);
      return idx >= 0
        ? prev.map(c => c.id === contact.id ? { ...c, ...contact } : c)
        : [contact, ...prev];
    });
    setSelectedId(contact.id);

    try {
      if (isNew) {
        const res = await api('/api/contacts', { method: 'POST', body: JSON.stringify(contact) });
        const data = await res.json().catch(() => ({}));
        // A 200 carrying no contact is a FAILURE: the row would keep a client-invented id,
        // and every later edit would address a record the server has never heard of.
        if (!data.contact) throw new Error('The server did not return the saved contact.');
        setContacts(prev => prev.map(c => c.id === contact.id ? { ...data.contact, inCrm: isPersonal ? undefined : false, lifetimeValue: isPersonal ? undefined : 0 } : c));
        setSelectedId(data.contact.id);
      } else {
        await api(`/api/contacts/${contact.id}`, { method: 'PUT', body: JSON.stringify(contact) });
      }
    } catch (err) {
      setContacts(snapshot);
      setSelectedId(previousSelected);
      showErrorToast(describeWriteFailure(err, isNew ? 'add that contact' : 'save that contact'));
      return;                         // the form stays open, holding what she typed
    }
    setShowForm(false);
    setEditingContact(null);
  }

  // The DELETE itself, sent by the delayed delete above once the Undo window has passed. It
  // throws on failure (api() does), and useDelayedDelete then restores the contact and says why.
  async function deleteContact(contact, { keepalive = false } = {}) {
    await api(`/api/contacts/${contact.id}`, { method: 'DELETE', keepalive });
  }

  async function markExisting(id) {
    const snapshot = contacts;
    setContacts(prev => prev.map(c => c.id === id ? { ...c, isNew: false } : c));
    try {
      await api(`/api/contacts/${id}`, { method: 'PUT', body: JSON.stringify({ isNew: false }) });
    } catch (err) {
      setContacts(snapshot);
      showErrorToast(describeWriteFailure(err, 'mark that contact as existing'));
    }
  }

  // A tag toggled in the drawer. Personal: one category at a time.
  async function toggleTag(contact, tag) {
    const has = (contact.tags || []).includes(tag);
    const tags = isPersonal
      ? [...(has ? [] : [tag]), ...(contact.tags || []).filter(t => !PERSONAL_CATEGORIES.includes(t))]
      : has ? contact.tags.filter(t => t !== tag) : [...(contact.tags || []), tag];
    const snapshot = contacts;
    setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, tags } : c));
    try {
      await api(`/api/contacts/${contact.id}`, { method: 'PUT', body: JSON.stringify({ tags }) });
    } catch (err) {
      setContacts(snapshot);
      showErrorToast(describeWriteFailure(err, has ? `take off the tag ${tag}` : `add the tag ${tag}`));
    }
  }

  // Add to CRM: onto the workspace's default board, at its first stage (the CRM's own endpoint).
  const [crmBusy, setCrmBusy] = useState(null);
  async function addToCrm(contact) {
    if (contact.inCrm) { navigateTo('/crm'); return; }
    setCrmBusy(contact.id);
    try {
      const { boards = [] } = await (await api('/api/crm-boards')).json();
      const board = boards.find(b => b.isDefault) || boards[0];
      if (!board) {
        showToast('Set up a CRM board first; the CRM page is open.', { type: 'info' });
        navigateTo('/crm');
        return;
      }
      await api(`/api/crm-boards/${board.id}/contacts`, { method: 'POST', body: JSON.stringify({ contactId: contact.id }) });
      setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, inCrm: true } : c));
      showToast(`${contact.name} added to the CRM, ${board.name}.`, { type: 'success' });
    } catch (err) {
      showErrorToast(describeWriteFailure(err, 'add them to the CRM'));
    } finally {
      setCrmBusy(null);
    }
  }

  const selectedContact = visible.find(c => c.id === selectedId) ?? null;
  const openAdd = () => { setEditingContact(null); setShowForm(true); };

  // ── Header ──────────────────────────────────────────────────────────────────
  const clientCount = visible.filter(c => (c.tags || []).includes('Client')).length;
  const portalCount = visible.filter(c => c.clientPortal).length;
  const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  const subline = !loaded ? 'Loading your contacts…' : contactsLoadFailed ? 'Your contacts could not be loaded.'
    : isPersonal ? plural(visible.length, 'contact')
      : `${plural(visible.length, 'contact')} · ${clientCount} client · ${portalCount} on client portal`;

  // ── Tabs ────────────────────────────────────────────────────────────────────
  const tabs = isPersonal
    ? [{ id: 'All', label: 'All', color: 'pastel-periwinkle', count: visible.length },
      ...PERSONAL_CATEGORIES.filter(cat => tagCounts[cat] || tagFilter === cat)
        .map(cat => ({ id: cat, label: cat, color: tagColor(cat), count: tagCounts[cat] || 0 }))]
    : [{ id: 'All', label: 'All', color: 'pastel-periwinkle', count: visible.length },
      ...[...new Set([...allTags, ...tagsInUse])]
        .filter(t => tagCounts[t] || customTags.includes(t) || tagFilter === t)
        .sort((a, b) => (a === 'Client' ? -1 : b === 'Client' ? 1 : 0))
        .map(t => ({ id: t, label: t, color: tagColor(t, tagColors), count: tagCounts[t] || 0 })),
      { id: 'New', label: 'New', color: tagColor('New'), count: newCount },
      ...(untaggedCount || tagFilter === 'Untagged' ? [{ id: 'Untagged', label: 'Untagged', color: 'pastel-neutral', count: untaggedCount }] : [])];

  const emptyTitle = search.trim()
    ? `No contacts match “${search.trim()}”`
    : tagFilter === 'New' ? 'No new contacts'
      : tagFilter === 'Untagged' ? 'No untagged contacts'
        : tagFilter === 'All' ? 'No contacts yet'
          : `No contacts tagged ${tagFilter} yet`;

  const cardFor = (c) => {
    const primary = primaryTag(c, isPersonal);
    const color = tagColor(primary, tagColors);
    const value = isPersonal ? null : valueText(c);
    return (
      <Card
        key={c.id}
        className="contacts-card"
        color={color}
        label={primary}
        meta={c.clientPortal ? <span className="contacts-portal-pill">Portal</span> : null}
        onClick={(e) => { if (!e.target.closest('button, a')) { setStartWith(null); setSelectedId(c.id); } }}
        footer={<>
          {/* Aidey's composer, in the contact's pop-up (Carla, 2026-10-22): mailto: did nothing in the app. */}
          <Button variant="confirm" size="sm" disabled={!c.email} title={c.email ? undefined : 'This contact has no email address'}
            onClick={() => { setStartWith('email'); setSelectedId(c.id); }}>Email</Button>
          {!isPersonal && c.inCrm !== undefined && (
            <Button variant="create" size="sm" onClick={() => addToCrm(c)} disabled={crmBusy === c.id}>
              {crmBusy === c.id ? 'Adding…' : c.inCrm ? 'In CRM ✓' : 'Add to CRM'}
            </Button>
          )}
        </>}
      >
        <div className="contacts-card__who">
          <Avatar name={c.name} size={44} color={color} />
          <div className="contacts-card__names">
            <button type="button" className="contacts-card__open" onClick={() => { setStartWith(null); setSelectedId(c.id); }}>{c.name}</button>
            <span className="contacts-card__company">{c.company || 'No company'}</span>
          </div>
        </div>
        <span className={c.email ? 'contacts-card__email' : 'contacts-card__email contacts-card__email--none'} title={c.email || undefined}>
          {c.email || 'No email'}
        </span>
        <span className="contacts-pills">
          <span className="contacts-pill">{c.lastContactedAt ? `Last contact ${lastContactLabel(c.lastContactedAt).toLowerCase()}` : 'No contact yet'}</span>
          {value && <span className="contacts-pill contacts-pill--value">{value}</span>}
        </span>
      </Card>
    );
  };

  const columns = [
    { key: 'name', label: 'Name', role: 'primary', width: 'minmax(0, 2fr)' },
    { key: 'email', label: 'Email', truncate: true, width: 'minmax(0, 1.6fr)' },
    { key: 'tags', label: isPersonal ? 'Category' : 'Tags', width: 'minmax(0, 1.2fr)' },
    { key: 'last', label: 'Last contact', width: 'minmax(0, 0.9fr)' },
    ...(isPersonal ? [] : [{ key: 'value', label: 'Value', role: 'status', align: 'end', width: 'minmax(0, 0.9fr)' }]),
  ];

  // The toolbar's pieces, defined once: on a phone they sit in the Filter sheet (fresh-eyes item 21),
  // elsewhere in the toolbar as before.
  const SORT_OPTIONS = [
    { value: 'recent', label: 'Recently contacted' },
    { value: 'name', label: 'Name A to Z' },
    ...(isPersonal ? [] : [{ value: 'value', label: 'Lifetime value' }]),
  ];
  const filterControls = (
    <div className="contacts-filters">
      <TabGroup tabs={tabs} value={tagFilter} onChange={setTagFilter} label={isPersonal ? 'Categories' : 'Tags'} idPrefix="contacts" />
      {!isPersonal && (
        <span className="contacts-filters__extra">
          <Chip
            data-testid="chip-needs-follow-up"
            selected={tagFilter === FOLLOW_UP_FILTER}
            color="pastel-orange"
            onClick={() => setTagFilter(tagFilter === FOLLOW_UP_FILTER ? 'All' : FOLLOW_UP_FILTER)}
          >
            Needs follow up{followUps.total > 0 && ` (${followUps.total})`}
          </Chip>
          <button type="button" className="contacts-edit-tags ui-hit" onClick={() => setShowTagEditor(true)}>Edit tags</button>
        </span>
      )}
    </div>
  );
  const viewControls = tagFilter !== FOLLOW_UP_FILTER && (
    <div className="contacts-view">
      <label className="contacts-sort">Sort
        <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort contacts" options={SORT_OPTIONS} />
      </label>
      <SegmentedControl label="Show as" value={view} onChange={setView}
        options={[{ value: 'cards', label: 'Cards' }, { value: 'list', label: 'List' }]} />
    </div>
  );
  const searchBox = (
    <SearchInput
      value={search}
      onChange={e => setSearch(e.target.value)}
      placeholder="Search by name, email, company or tag…"
      label="Search contacts"
    />
  );
  // What the phone's Filter line names: the filters now applied, never the defaults.
  const activeFilters = [
    tagFilter === FOLLOW_UP_FILTER ? 'Needs follow up' : tagFilter !== 'All' ? tagFilter : null,
    tagFilter !== FOLLOW_UP_FILTER && sort !== 'recent' ? SORT_OPTIONS.find((o) => o.value === sort)?.label : null,
  ].filter(Boolean);

  const editForm = showForm && (
    <ContactFormModal
      contact={editingContact}
      onSave={saveContact}
      onClose={() => { setShowForm(false); setEditingContact(null); }}
      availableTags={allTags}
      isPersonal={isPersonal}
      contacts={contacts}
      tagColors={tagColors}
    />
  );

  // The full contact page, /contacts/<id> (Carla, 2026-10-21). Waits for the contacts to load; one
  // that is not there (deleted, merged, someone else's) says so, never an empty page.
  if (pageAt) {
    const pageContact = visible.find((c) => String(c.id) === String(pageAt.id)) || null;
    const crmControl = pageContact && !isPersonal && pageContact.inCrm !== undefined ? (
      <button type="button" className="contacts-linkbtn" onClick={() => addToCrm(pageContact)} disabled={crmBusy === pageContact.id}>
        {pageContact.inCrm ? 'In CRM ✓' : crmBusy === pageContact.id ? 'Adding…' : 'Add to CRM'}
      </button>
    ) : null;
    return (
      <>
        {pageContact ? (
          <ContactPage contact={pageContact} isPersonal={isPersonal} tagColors={tagColors} crm={crmControl}
            tab={pageAt.tab} onTab={(tab) => showPageTab(pageAt.id, tab)} onBack={backToList}
            onEdit={() => { setEditingContact(pageContact); setShowForm(true); }} />
        ) : (
          <PageLayout data-page="contact">
            <button type="button" className="contacts-back" onClick={backToList}><span aria-hidden="true">← </span>Contacts</button>
            {contactsLoadFailed ? (
              <ErrorState title="Contacts didn't load" message="This contact could not be shown because your contacts did not load." onRetry={refresh} />
            ) : loaded === false ? (
              <Skeleton lines={4} label="Loading this contact" />
            ) : (
              <EmptyState title="This contact is no longer available">It may have been deleted or merged into another contact.</EmptyState>
            )}
          </PageLayout>
        )}
        {editForm}
      </>
    );
  }

  return (
    <PageLayout data-page="contacts">
      <PageHeader
        eyebrow={{ label: isPersonal ? 'Personal workspace' : 'Business workspace', color: isPersonal ? 'group-my-work' : 'group-clients' }}
        title="Contacts"
        subline={subline}
        primary={<HeaderButton primary onClick={openAdd}>+ Add contact</HeaderButton>}
        secondary={isPersonal ? [] : [{ label: 'Import CSV', onClick: () => setShowImport(true) }]}
      />

      {!isPersonal && dupes.canMerge && openDupes.length > 0 && (
        <div data-testid="contacts-duplicates">
          {phone ? (
            // Fresh-eyes item 21: on a phone the notice is one slim line, its action beside it.
            <p className="contacts-dupes-line">
              <span>{openDupes.length === 1 ? '1 possible duplicate.' : `${openDupes.length} possible duplicates.`}</span>
              <button type="button" className="contacts-linkbtn" onClick={() => setShowMerge(true)}>Review & merge</button>
            </p>
          ) : (
            <Banner tone="info"
              action={<Button variant="secondary" size="sm" onClick={() => setShowMerge(true)}>Review & merge</Button>}>
              {openDupes.length === 1 ? '1 possible duplicate.' : `${openDupes.length} possible duplicates.`}
            </Banner>
          )}
        </div>
      )}

      {phone ? (
        <PhoneListFilters search={searchBox} active={activeFilters} title="Filter contacts" done="Show contacts">
          {filterControls}
          {viewControls}
        </PhoneListFilters>
      ) : (
        <div className="contacts-toolbar">
          <div className="contacts-toolbar__row">
            {filterControls}
            {viewControls}
          </div>
          {searchBox}
        </div>
      )}

      {/* A FAILED LOAD IS NOT AN EMPTY ADDRESS BOOK. Checked BEFORE the empty branch, because an
          empty list is the answer only once a load has actually succeeded. */}
      {tagFilter === FOLLOW_UP_FILTER && !isPersonal ? (
        <FollowUpList state={followUps} search={search} contacts={contacts} onOpenContact={(id) => setSelectedId(id)} />
      ) : contactsLoadFailed ? (
        <div data-testid="contacts-load-failed" className="contacts-failed">
          <ErrorState
            title="Your contacts could not be loaded."
            message="This is not an empty list, we could not reach them. Check your connection and try again."
            onRetry={refresh}
          />
        </div>
      ) : !loaded ? (
        <div className="contacts-grid" aria-busy="true">
          {[0, 1, 2].map(i => <Skeleton key={i} height={220} radius={20} label="Loading contacts" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          action={<Button variant="create" size="sm" onClick={openAdd}>+ Add contact</Button>}
        >
          {visible.length === 0 ? 'Add the people you work with, or import them from a CSV.' : 'Try a different search or tag, or add someone new.'}
        </EmptyState>
      ) : view === 'cards' ? (
        <section className="contacts-grid" aria-label="Contacts">
          {filtered.map(cardFor)}
        </section>
      ) : (
        <List label="Contacts" columns={columns}>
          {filtered.map(c => {
            const primary = primaryTag(c, isPersonal);
            const value = isPersonal ? null : valueText(c);
            return (
              <ListRow
                key={c.id}
                onOpen={() => setSelectedId(c.id)}
                openLabel={`Open ${c.name}`}
                cells={{
                  name: <RowPrimary leading={<Avatar name={c.name} size={36} color={tagColor(primary, tagColors)} />} name={c.name} sub={c.company || undefined} />,
                  email: c.email || '',
                  tags: (
                    <span className="contacts-tags">
                      {(c.tags || []).length
                        ? orderedTags(c.tags).map(t => <span key={t} className="contacts-tag" style={{ '--ui-bg': v(tagColor(t, tagColors)) }}>{t}</span>)
                        : <span className="contacts-tag" style={{ '--ui-bg': v(tagColor(primary, tagColors)) }}>{primary}</span>}
                    </span>
                  ),
                  last: lastContactLabel(c.lastContactedAt),
                  ...(isPersonal ? {} : { value: <span className="contacts-value">{value ? value.replace(/ lifetime$/, '') : ''}</span> }),
                }}
                meta={[c.email, c.lastContactedAt ? `Last contact ${lastContactLabel(c.lastContactedAt).toLowerCase()}` : 'No contact yet'].filter(Boolean).join(' · ')}
              />
            );
          })}
        </List>
      )}

      {selectedContact && (
        <ContactDrawer
          contact={selectedContact}
          isPersonal={isPersonal}
          tagColors={tagColors}
          availableTags={allTags}
          canDelete={canDeleteContacts}
          crm={!isPersonal && selectedContact.inCrm !== undefined ? (
            <button type="button" className="contacts-linkbtn"
              onClick={() => addToCrm(selectedContact)} disabled={crmBusy === selectedContact.id}>
              {selectedContact.inCrm ? 'In CRM ✓' : crmBusy === selectedContact.id ? 'Adding…' : 'Add to CRM'}
            </button>
          ) : null}
          startWith={startWith}
          onClose={() => { setSelectedId(null); setStartWith(null); }}
          onEdit={() => { setEditingContact(selectedContact); setSelectedId(null); setShowForm(true); }}
          onDelete={() => setDeleteConfirm(selectedContact)}
          onMarkExisting={() => markExisting(selectedContact.id)}
          onToggleTag={(t) => toggleTag(selectedContact, t)}
          onOpenPage={(id, tab) => openContactPage(id, tab)}
        />
      )}

      {editForm}

      {!isPersonal && (
        <TagEditor
          open={showTagEditor}
          onClose={() => setShowTagEditor(false)}
          tagsInUse={tagsInUse}
          customTags={customTags}
          tagColors={tagColors}
          prefsError={prefsError}
          onRetryPrefs={loadPrefs}
          tagCounts={tagCounts}
          onAddTag={addCustomTag}
          onRecolour={recolourTag}
          canRecolour={canRecolour}
          onRemoveTag={removeTag}
        />
      )}

      {!isPersonal && (
        <MergeReview open={showMerge} pairs={openDupes} onClose={() => setShowMerge(false)}
          onMerge={(keep, other) => merger.remove({ id: other.id, keep, other })} />
      )}

      {!isPersonal && (
        <ImportContacts
          open={showImport}
          onClose={() => setShowImport(false)}
          contacts={contacts}
          onImported={refresh}
        />
      )}

      {/* What a delete really removes (schema, 2026-10-08): the contact, its CRM cards, and its
          client portal with the portal's messages, files and sessions. Invoices, proposals,
          contracts, bookings, tasks, notes and questionnaire answers are KEPT, no longer linked. */}
      <ConfirmDialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        itemType="contact"
        title={`Delete ${deleteConfirm?.name || 'this contact'}?`}
        confirmLabel="Delete contact"
        message={
          `This deletes the contact${isPersonal ? '' : ', their cards in the CRM, and their client portal with its messages and files'}. `
          + (isPersonal ? 'Notes and tasks that mention them are kept. ' : 'Invoices, proposals, contracts, bookings, tasks and notes are kept, but no longer linked to them. ')
          + 'You can undo for a few seconds afterwards.'
        }
        onConfirm={() => {
          const c = deleteConfirm;
          setSelectedId(null);
          remover.remove(c);
        }}
      />
    </PageLayout>
  );
}

export default ContactsDashboard;
